import { useCallback, useMemo } from "react";
import WebService from "../../../Services/WebService";
import type { Slide, Response } from "../../ManageQuestions/types";

export type AttemptMode = "training" | "certificate" | "simulator";
export type AttemptStatus = "in_progress" | "graded" | "cancelled";

export type AttemptAnswer = {
  user_response: any;
  auto_grade?: { is_correct?: boolean; awarded_score?: number };
  answered_at?: string;
};

export type AttemptSnapshot = {
  id: string;
  title: string;
  ui: {
    show_stepper: boolean;
    show_timer: boolean;
    locale: string;
    accessibility_mode: boolean;
  };
  slides: Slide[];
  responses: Response[];
  instructions_player?: {
    inline_zoom: boolean;
    auto_advance: boolean;
    show_progress: boolean;
    show_nav_buttons: boolean;
    require_complete_before_questions: boolean;
  };
  attempt: {
    attempt_uuid: string | null;
    mode: AttemptMode;
    scope: "lesson" | "course";
    course_id?: number | null;
    course_level_id?: number | null;
    course_lesson_id?: number | null;
    enrollment_id?: number | null;
    subscription_id?: number | null;
    random_seed?: string | null;
    passing_score_used: number;
    timing?: {
      allowed_seconds?: number | null;
      started_at?: string;
      must_submit_by?: string | null;
    };
    navigation?: { current_index: number; total: number };
    answers: Record<string, AttemptAnswer>;
    totals: {
      total_questions: number;
      answered: number;
      correct: number;
      score_percent: number;
    };
    status: AttemptStatus;
    course_lesson_uuid: string | null;
    runtime_params: {
      call_sign: "";
      atis: "";
      altitude: "";
    };
    is_mobile?: boolean;
  };
  runtime_params: {
    call_sign: "";
    atis: "";
    altitude: "";
  };
};

export type AttemptRow = {
  id: number;
  uuid: string;
  mode: AttemptMode;
  user_id: number;
  course_id?: number | null;
  training_course_enrollment_id?: number | null;
  user_subscription_id?: number | null;
  course_level_id?: number | null;
  course_lesson_id?: number | null;
  attempt_snapshot_json: AttemptSnapshot;
  total_questions: number;
  correct: number;
  score_percent: number | string;
  passed: boolean;
  status: AttemptStatus;
  started_at: string;
  submitted_at?: string | null;
  runtime_params: {
    call_sign: "";
    atis: "";
    altitude: "";
  };
  is_mobile?: boolean;
};

export const percent = (num: number, den: number) =>
  den > 0 ? Math.round((num / den) * 100) : 0;

export const useAttemptEngine = (
  attempt: AttemptRow | null,
  setAttempt: (a: AttemptRow) => void,
  apiBase: string, // e.g. "/training/attempts"
) => {
  const snapshot = attempt?.attempt_snapshot_json || null;
  const slides = snapshot?.slides || [];
  const responses = snapshot?.responses || [];
  const idx = snapshot?.attempt?.navigation?.current_index || 0;
  const currentSlide: Slide | undefined = slides[idx];
  const currentResponse: Response | undefined = useMemo(
    () =>
      currentSlide
        ? responses.find((r) => r.anchored_to_slide === currentSlide.id)
        : undefined,
    [responses, currentSlide],
  );

  const answeredMap = snapshot?.attempt?.answers || {};
  const hasAnsweredCurrent = !!(
    currentResponse && answeredMap[currentResponse.id]
  );

  const go = useCallback(
    (nextIndex: number) => {
      if (!attempt || !snapshot) return;
      const clamped = Math.max(0, Math.min(slides.length - 1, nextIndex));
      const next = structuredClone(attempt);
      (next.attempt_snapshot_json.attempt.navigation as any).current_index =
        clamped;
      next.runtime_params = attempt.runtime_params;
      next.attempt_snapshot_json.attempt.runtime_params =
        attempt.attempt_snapshot_json.attempt.runtime_params;
      setAttempt(next);
    },
    [attempt, snapshot, slides.length, setAttempt],
  );

  const prev = () => go(idx - 1);
  const next = () => go(idx + 1);

  const submitAnswer = useCallback(
    async (uuid: string, responseId: string, user_response: any) => {
      const res: any = await WebService.postAPI({
        action: `${apiBase}/${uuid}/answer`,
        body: { response_id: responseId, user_response },
      });

      setAttempt({
        ...(attempt as any),
        attempt_snapshot_json: {
          ...snapshot!,
          attempt: {
            ...snapshot!.attempt,
            runtime_params: snapshot!.attempt.runtime_params,
            answers: { ...snapshot!.attempt.answers, [responseId]: res.answer },
            totals: res.totals,
          },
        },
        correct: res.totals?.correct ?? (attempt as any)?.correct ?? 0,
        score_percent:
          res.totals?.score_percent ?? (attempt as any)?.score_percent ?? 0,
      });

      console.log("attempt", attempt);
    },
    [attempt, setAttempt, snapshot, apiBase],
  );

  const finalizeAttempt = useCallback(
    async (uuid: string) => {
      const res: any = await WebService.postAPI({
        action: `${apiBase}/${uuid}/finalize`,
        body: {},
      });
      setAttempt(res);
      return res;
    },
    [setAttempt, apiBase],
  );

  return {
    snapshot,
    slides,
    responses,
    idx,
    currentSlide,
    currentResponse,
    hasAnsweredCurrent,
    go,
    prev,
    next,
    submitAnswer,
    finalizeAttempt,
  };
};
